import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { useRouters } from '../api/queries';
import type { FindingSeverity, HourProfile, PerformanceReport, RouterDto, SeriesPoint } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { Badge, Card, Empty, ErrorAlert, Field, Spinner, Stat } from '../components/ui';
import { useCurrentRouter } from '../lib/current-router';
import { formatDateTime, formatKbps, formatNumber, formatRelative } from '../lib/format';

const SEVERITY_META: Record<FindingSeverity, { label: string; tone: 'bad' | 'warn' | 'ok' | 'info' }> = {
  critical: { label: 'Crítico', tone: 'bad' },
  warning: { label: 'Atención', tone: 'warn' },
  ok: { label: 'Sin problema', tone: 'ok' },
  info: { label: 'Nota', tone: 'info' },
};

export function PerformancePage() {
  const { hasRole } = useAuth();
  const routers = useRouters();
  // El router de trabajo de la barra superior, como en el resto de pantallas.
  const { routerId } = useCurrentRouter();
  const [days, setDays] = useState(7);

  const offsetMinutes = -new Date().getTimezoneOffset();

  const report = useQuery({
    queryKey: ['performance', routerId, days, offsetMinutes],
    queryFn: () =>
      api.get<PerformanceReport>(`/performance/${routerId}/report`, { days, offsetMinutes }),
    enabled: Boolean(routerId),
    // Una muestra nueva por minuto: refrescar más rápido no aporta nada.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const data = report.data;
  const router = routers.data?.find((item) => item.id === routerId) ?? null;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Horas pico</h1>
          <p>
            Por qué la red va lenta cuando más gente la usa: tráfico del enlace, CPU del router y
            quién consume, muestreado cada minuto.
          </p>
        </div>
        <div className="toolbar">
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            <option value={1}>Último día</option>
            <option value={3}>Últimos 3 días</option>
            <option value={7}>Última semana</option>
            <option value={14}>Últimas 2 semanas</option>
            <option value={30}>Último mes</option>
          </select>
        </div>
      </div>

      <ErrorAlert error={report.error} />

      {!routerId || report.isLoading ? (
        <Spinner label="Analizando…" />
      ) : !data ? null : (
        <>
          {router && hasRole('SUPER_ADMIN') && <LinkConfigCard router={router} report={data} />}

          <NowStats report={data} />

          <Card
            title="Diagnóstico"
            actions={
              <span className="muted" style={{ fontSize: 12 }}>
                {formatNumber(data.sampleCount)} muestras
                {data.since ? ` desde ${formatRelative(data.since)}` : ''}
                {data.peakHours.length > 0 && ` · hora pico: ${data.peakHours.map(fmtHour).join(', ')}`}
              </span>
            }
          >
            <div className="card-body">
              {data.findings.length === 0 ? (
                <Empty>Sin hallazgos.</Empty>
              ) : (
                <div className="findings">
                  {data.findings.map((finding) => (
                    <div key={finding.code} className={`finding ${finding.severity}`}>
                      <div className="finding-head">
                        <Badge tone={SEVERITY_META[finding.severity].tone}>
                          {SEVERITY_META[finding.severity].label}
                        </Badge>
                        <strong>{finding.title}</strong>
                      </div>
                      <p className="muted">{finding.detail}</p>
                      {finding.recommendations.length > 0 && (
                        <ul className="finding-recs">
                          {finding.recommendations.map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <div className="grid grid-two">
            <Card title="Perfil por hora del día">
              <div className="card-body">
                <HourlyChart hourly={data.hourly} peakHours={data.peakHours} capacityKbps={data.router.wanDownloadKbps} />
              </div>
            </Card>
            <Card title="Últimas 24 horas">
              <div className="card-body">
                <SeriesChart series={data.series} capacityKbps={data.router.wanDownloadKbps} />
              </div>
            </Card>
          </div>

          <Card
            title="Quién consume en hora pico"
            actions={
              <span className="muted" style={{ fontSize: 12 }}>
                media de los minutos en que aparecen entre los 10 primeros
              </span>
            }
          >
            {data.topConsumers.length === 0 ? (
              <Empty>Todavía no hay datos de consumo por usuario en hora pico.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Servicio</th>
                      <th className="num">Bajada</th>
                      <th className="num">Subida</th>
                      <th className="num">% del enlace</th>
                      <th className="num">Minutos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topConsumers.map((user) => (
                      <tr key={`${user.service}:${user.username}`}>
                        <td className="mono">{user.username}</td>
                        <td>{user.service === 'HOTSPOT' ? 'HotSpot' : 'PPPoE'}</td>
                        <td className="num">{formatKbps(user.avgDownKbps)}</td>
                        <td className="num">{formatKbps(user.avgUpKbps)}</td>
                        <td className="num">
                          <ShareBar percent={user.sharePercent} />
                        </td>
                        <td className="num faint">{formatNumber(user.appearances)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </main>
  );
}

function fmtHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

// ------------------------------------------------------------------ enlace

function LinkConfigCard({ router, report }: { router: RouterDto; report: PerformanceReport }) {
  const queryClient = useQueryClient();
  const [wanInterface, setWanInterface] = useState(router.wanInterface ?? '');
  const [downMbps, setDownMbps] = useState(router.wanDownloadKbps ? String(router.wanDownloadKbps / 1000) : '');
  const [upMbps, setUpMbps] = useState(router.wanUploadKbps ? String(router.wanUploadKbps / 1000) : '');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setWanInterface(router.wanInterface ?? '');
    setDownMbps(router.wanDownloadKbps ? String(router.wanDownloadKbps / 1000) : '');
    setUpMbps(router.wanUploadKbps ? String(router.wanUploadKbps / 1000) : '');
  }, [router.id, router.wanInterface, router.wanDownloadKbps, router.wanUploadKbps]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<RouterDto>(`/routers/${router.id}`, {
        wanInterface,
        wanDownloadKbps: Math.round(Number(downMbps || 0) * 1000),
        wanUploadKbps: Math.round(Number(upMbps || 0) * 1000),
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['routers'] });
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
    },
    onError: setError,
  });

  const interfaces = report.latest?.interfaces ?? [];
  const detected = report.latest?.wanInterface ?? null;

  return (
    <Card
      title="Enlace a internet"
      actions={
        <span className="muted" style={{ fontSize: 12 }}>
          {detected ? `WAN en uso: ${detected}` : 'WAN sin detectar todavía'}
        </span>
      }
    >
      <div className="card-body">
        <ErrorAlert error={error} onDismiss={() => setError(null)} />
        <div className="form-row">
          <Field label="Interfaz WAN" hint="Vacío = detectar automáticamente la de más tráfico entrante">
            <select value={wanInterface} onChange={(event) => setWanInterface(event.target.value)}>
              <option value="">Automática{detected ? ` (${detected})` : ''}</option>
              {interfaces
                .filter((iface) => !iface.n.startsWith('<'))
                .map((iface) => (
                  <option key={iface.n} value={iface.n}>
                    {iface.n} · ↓{formatKbps(iface.rx)} ↑{formatKbps(iface.tx)}
                  </option>
                ))}
              {wanInterface && !interfaces.some((iface) => iface.n === wanInterface) && (
                <option value={wanInterface}>{wanInterface}</option>
              )}
            </select>
          </Field>
          <Field label="Bajada contratada (Mbps)" hint="Lo que da el proveedor; permite calcular la saturación">
            <input type="number" min={0} step="0.5" value={downMbps} onChange={(event) => setDownMbps(event.target.value)} />
          </Field>
          <Field label="Subida contratada (Mbps)">
            <input type="number" min={0} step="0.5" value={upMbps} onChange={(event) => setUpMbps(event.target.value)} />
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="button" className="primary" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? <Spinner label="Guardando…" /> : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ ahora mismo

function NowStats({ report }: { report: PerformanceReport }) {
  const latest = report.latest;
  if (!latest) {
    return (
      <div className="alert warn">
        Todavía no hay muestras de este router. El muestreo corre cada minuto en segundo plano:
        en unos minutos aparecerán los primeros datos.
      </div>
    );
  }
  const capacity = report.router.wanDownloadKbps;
  const utilization = capacity ? Math.round((latest.downKbps / capacity) * 100) : null;

  return (
    <div className="grid grid-stats">
      <Stat
        label="Bajada ahora"
        value={formatKbps(latest.downKbps)}
        hint={utilization !== null ? `${utilization}% del enlace` : `interfaz ${latest.wanInterface ?? '—'}`}
      />
      <Stat label="Subida ahora" value={formatKbps(latest.upKbps)} hint={`hace ${formatRelative(latest.sampledAt)}`} />
      <Stat
        label="CPU del router"
        value={`${latest.cpuLoad}%`}
        hint={<span className={latest.cpuLoad >= 80 ? 'badge bad' : latest.cpuLoad >= 60 ? 'badge warn' : 'badge ok'}>
          {latest.cpuLoad >= 80 ? 'al límite' : latest.cpuLoad >= 60 ? 'cargada' : 'holgada'}
        </span>}
      />
      <Stat label="Memoria usada" value={`${latest.memoryUsedPercent}%`} hint={`API en ${latest.latencyMs} ms`} />
      <Stat
        label="Conectados"
        value={formatNumber(latest.hotspotOnline + latest.pppoeOnline)}
        hint={`${latest.hotspotOnline} HotSpot · ${latest.pppoeOnline} PPPoE`}
      />
    </div>
  );
}

// ------------------------------------------------------------------ gráficas

function ShareBar({ percent }: { percent: number }) {
  return (
    <span className="share-bar" title={`${percent}%`}>
      <span className="share-bar-fill" style={{ width: `${Math.min(100, percent)}%` }} />
      <span className="share-bar-label">{percent}%</span>
    </span>
  );
}

const CHART_W = 640;
const CHART_H = 220;
const PAD = { top: 12, right: 12, bottom: 28, left: 52 };

function niceMax(value: number): number {
  if (value <= 0) return 1000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

function HourlyChart({
  hourly,
  peakHours,
  capacityKbps,
}: {
  hourly: HourProfile[];
  peakHours: number[];
  capacityKbps: number | null;
}) {
  const hasData = hourly.some((hour) => hour.samples > 0);
  if (!hasData) return <Empty>Sin muestras todavía.</Empty>;

  const maxValue = niceMax(Math.max(...hourly.map((hour) => hour.p95DownKbps), capacityKbps ?? 0));
  const innerW = CHART_W - PAD.left - PAD.right;
  const innerH = CHART_H - PAD.top - PAD.bottom;
  const slot = innerW / 24;
  const y = (value: number) => PAD.top + innerH - (value / maxValue) * innerH;

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label="Tráfico de bajada por hora del día">
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
          <g key={fraction}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(maxValue * fraction)} y2={y(maxValue * fraction)} className="chart-grid" />
            <text x={PAD.left - 6} y={y(maxValue * fraction) + 4} className="chart-axis" textAnchor="end">
              {formatKbps(Math.round(maxValue * fraction)).replace(' ', '')}
            </text>
          </g>
        ))}
        {capacityKbps && capacityKbps <= maxValue && (
          <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(capacityKbps)} y2={y(capacityKbps)} className="chart-capacity" />
        )}
        {hourly.map((hour) => {
          const x = PAD.left + hour.hour * slot;
          const isPeak = peakHours.includes(hour.hour);
          return (
            <g key={hour.hour}>
              <title>
                {`${fmtHour(hour.hour)} · media ${formatKbps(hour.avgDownKbps)} · p95 ${formatKbps(hour.p95DownKbps)} · CPU ${hour.avgCpu}% · ${hour.avgOnline} conectados · ${hour.samples} muestras`}
              </title>
              <rect x={x + 2} width={slot - 4} y={y(hour.p95DownKbps)} height={Math.max(0, innerH + PAD.top - y(hour.p95DownKbps))} className={isPeak ? 'chart-bar peak p95' : 'chart-bar p95'} />
              <rect x={x + 2} width={slot - 4} y={y(hour.avgDownKbps)} height={Math.max(0, innerH + PAD.top - y(hour.avgDownKbps))} className={isPeak ? 'chart-bar peak' : 'chart-bar'} />
              {hour.hour % 3 === 0 && (
                <text x={x + slot / 2} y={CHART_H - 8} className="chart-axis" textAnchor="middle">
                  {hour.hour}h
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="chart-legend">
        <span><i className="swatch bar" /> media</span>
        <span><i className="swatch bar p95" /> p95</span>
        <span><i className="swatch peak" /> hora pico</span>
        {capacityKbps && <span><i className="swatch capacity" /> contratado</span>}
      </div>
      <CpuStrip hourly={hourly} peakHours={peakHours} />
    </div>
  );
}

function CpuStrip({ hourly, peakHours }: { hourly: HourProfile[]; peakHours: number[] }) {
  return (
    <div className="cpu-strip" title="CPU media por hora">
      {hourly.map((hour) => {
        const tone = hour.samples === 0 ? 'empty' : hour.avgCpu >= 80 ? 'bad' : hour.avgCpu >= 60 ? 'warn' : 'ok';
        return (
          <span
            key={hour.hour}
            className={`cpu-cell ${tone}${peakHours.includes(hour.hour) ? ' peak' : ''}`}
            title={`${fmtHour(hour.hour)} · CPU ${hour.avgCpu}% (máx ${hour.maxCpu}%)`}
          >
            {hour.samples > 0 ? hour.avgCpu : ''}
          </span>
        );
      })}
      <span className="faint" style={{ fontSize: 11, marginLeft: 6 }}>CPU %</span>
    </div>
  );
}

function SeriesChart({ series, capacityKbps }: { series: SeriesPoint[]; capacityKbps: number | null }) {
  if (series.length < 2) return <Empty>Hacen falta al menos unos minutos de muestras.</Empty>;

  const maxValue = niceMax(Math.max(...series.map((point) => Math.max(point.down, point.up)), capacityKbps ?? 0));
  const innerW = CHART_W - PAD.left - PAD.right;
  const innerH = CHART_H - PAD.top - PAD.bottom;
  const t0 = new Date(series[0]!.t).getTime();
  const t1 = new Date(series[series.length - 1]!.t).getTime();
  const span = Math.max(1, t1 - t0);
  const x = (t: string) => PAD.left + ((new Date(t).getTime() - t0) / span) * innerW;
  const y = (value: number) => PAD.top + innerH - (value / maxValue) * innerH;
  const yCpu = (value: number) => PAD.top + innerH - (value / 100) * innerH;

  const path = (pick: (point: SeriesPoint) => number, scale: (value: number) => number) =>
    series.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.t).toFixed(1)},${scale(pick(point)).toFixed(1)}`).join(' ');

  const ticks: SeriesPoint[] = [];
  for (let index = 0; index < series.length; index += Math.max(1, Math.floor(series.length / 6))) ticks.push(series[index]!);

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label="Tráfico de las últimas 24 horas">
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
          <g key={fraction}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(maxValue * fraction)} y2={y(maxValue * fraction)} className="chart-grid" />
            <text x={PAD.left - 6} y={y(maxValue * fraction) + 4} className="chart-axis" textAnchor="end">
              {formatKbps(Math.round(maxValue * fraction)).replace(' ', '')}
            </text>
          </g>
        ))}
        {capacityKbps && capacityKbps <= maxValue && (
          <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(capacityKbps)} y2={y(capacityKbps)} className="chart-capacity" />
        )}
        <path d={path((point) => point.cpu, yCpu)} className="chart-line cpu" />
        <path d={path((point) => point.up, y)} className="chart-line up" />
        <path d={path((point) => point.down, y)} className="chart-line down" />
        {ticks.map((point) => (
          <text key={point.t} x={x(point.t)} y={CHART_H - 8} className="chart-axis" textAnchor="middle">
            {new Date(point.t).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
          </text>
        ))}
      </svg>
      <div className="chart-legend">
        <span><i className="swatch line down" /> bajada</span>
        <span><i className="swatch line up" /> subida</span>
        <span><i className="swatch line cpu" /> CPU (0-100%)</span>
        {capacityKbps && <span><i className="swatch capacity" /> contratado</span>}
        <span className="faint">· última muestra {formatDateTime(series[series.length - 1]!.t)}</span>
      </div>
    </div>
  );
}
